/**
 * Browser support detection.
 *
 * The app is supported ONLY on genuine Google Chrome and Microsoft Edge.
 * Every other browser — including other Chromium-based forks (Brave, Opera, Vivaldi,
 * Samsung Internet, Yandex, …) as well as Firefox and Safari — is treated as
 * unsupported and shown a warning banner.
 *
 * Detecting "genuine Chrome" is inherently best-effort: several Chromium forks
 * deliberately mask their user-agent to look identical to Chrome. We therefore
 * check for positive fork signals first (a dedicated brand, a `navigator.*` hook,
 * or a UA token) and only fall through to "chrome" when none of them match.
 * A fork that perfectly impersonates Chrome and exposes no distinguishing signal
 * (e.g. Arc) cannot be told apart and will be treated as Chrome.
 */

export function detectBrowser(nav) {
  const navigatorRef =
    nav || (typeof navigator !== 'undefined' ? navigator : undefined);

  if (!navigatorRef) {
    // Non-browser environment (SSR/tests): don't block.
    return { name: 'unknown', supported: true };
  }

  const ua = navigatorRef.userAgent || '';
  const uaData = navigatorRef.userAgentData;
  const brands =
    uaData && Array.isArray(uaData.brands)
      ? uaData.brands.map((b) => b.brand || '')
      : [];
  const hasBrand = (re) => brands.some((b) => re.test(b));

  // --- Known Chromium forks that masquerade as Chrome. Check these FIRST. ---

  // Brave hides its identity in the UA string and brand list; the only reliable
  // signal is the `navigator.brave` object it injects.
  if (navigatorRef.brave) return { name: 'brave', supported: false };

  if (/OPR\//.test(ua) || hasBrand(/Opera|OPR/i)) {
    return { name: 'opera', supported: false };
  }
  if (/SamsungBrowser\//.test(ua)) {
    return { name: 'samsung', supported: false };
  }
  if (/YaBrowser\//.test(ua) || hasBrand(/Yandex/i)) {
    return { name: 'yandex', supported: false };
  }
  if (/\bVivaldi\b/.test(ua) || hasBrand(/Vivaldi/i)) {
    return { name: 'vivaldi', supported: false };
  }
  if (/\bWhale\//.test(ua)) {
    return { name: 'whale', supported: false };
  }

  // --- Edge (Chromium). "Edg/" = desktop, "EdgA/"/"EdgiOS/" = mobile. ---
  if (/Edg(A|iOS)?\//.test(ua) || hasBrand(/Microsoft Edge/i)) {
    return { name: 'edge', supported: true };
  }

  // Legacy EdgeHTML Edge ("Edge/") is not Chromium and not supported.
  if (/Edge\//.test(ua)) {
    return { name: 'edge-legacy', supported: false };
  }

  // --- Genuine Chrome. Reached only after every known fork is excluded. ---
  // Require a positive Chrome signal (not a fork silently masking as Chrome we
  // failed to catch — those are indistinguishable and accepted as Chrome).
  if (hasBrand(/Google Chrome/i)) {
    return { name: 'chrome', supported: true };
  }
  if (/Chrome\/|CriOS\//.test(ua)) {
    return { name: 'chrome', supported: true };
  }

  // --- Everything else is unsupported. ---
  if (/Firefox\/|FxiOS\//.test(ua)) {
    return { name: 'firefox', supported: false };
  }
  if (/Safari\//.test(ua)) {
    return { name: 'safari', supported: false };
  }

  return { name: 'unknown', supported: false };
}

export function isSupportedBrowser(nav) {
  return detectBrowser(nav).supported;
}
